import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/errors/use-toast';
import { useUser } from '@/hooks/auth/use-user';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import { convertToWebP, isConvertibleToWebP, getOptimalConversionSettings } from '@/utils/image-conversion';
import { useCreditCalculator } from '@/hooks/useCreditCalculator';
// NOTE: Universal Storage imports removed - no automatic storage integration
import { FilterOption } from '@/components/ui/FilterDropdown';
import { ImageType } from '@/components/image-generator/FilterBar/TypeFilter';
import { QuestTriggers } from '@/hooks/onboarding/useQuestDetection';
import type { PrivateImageGeneratorItem } from './types';
import { generatePrivateImage } from '@/utils/generate-private-image';

interface UsePrivateImageGenerationOptions {
  imageDescription: string;
  width: string;
  height: string;
  selectedType: ImageType | null;
  selectedStyle: string | null;
  selectedInfluence: string | null;
  selectedBackground: string | null;
  styleOptions: FilterOption[];
  influenceOptions: FilterOption[];
  colorOptions: FilterOption[];
  backgroundOptions: FilterOption[];
  environmentOptions: FilterOption[];
  moodOptions: FilterOption[];
  selectedProjectId?: string | null;
}

/**
 * Hook for generating images and saving them to workbook_images with optional project assignment
 * workbook_id can be NULL (private) or a specific project ID
 */
export const usePrivateImageGeneration = (options: UsePrivateImageGenerationOptions) => {
  const {
    imageDescription,
    width,
    height,
    selectedType,
    selectedStyle,
    selectedInfluence,
    selectedBackground,
    styleOptions,
    influenceOptions,
    colorOptions,
    backgroundOptions,
    environmentOptions,
    moodOptions,
    selectedProjectId
  } = options;

  const { toast } = useToast();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { executeAIOperation, isLoading } = useAIOperationWrapper();
  
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<PrivateImageGeneratorItem | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);


  // Utility function to sanitize filename
  const sanitizeFilename = (filename: string): string => {
    return filename
      .replace(/\.[^/.]+$/, '')
      .replace(/[^a-zA-Z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 50);
  };

  // Function to get image dimensions
  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        resolve({ width: 0, height: 0 });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Function to save generated image to workbook_images with workbook_id = NULL
  const saveGeneratedImageToPrivateLibrary = async (
    imageUrl: string,
    generationPrompt: string,
    imageWidth: number,
    imageHeight: number
  ): Promise<PrivateImageGeneratorItem | null> => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      // Download the image from the URL
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'generated-image.webp', { type: 'image/webp' });

      // Process the file (WebP conversion if needed)
      let processedFile = file;
      let conversionInfo: string | null = null;

      if (file.type !== 'image/webp' && isConvertibleToWebP(file)) {
        try {
          const conversionSettings = getOptimalConversionSettings(file, 'workbook');
          const result = await convertToWebP(file, conversionSettings);
          processedFile = result.file;
          conversionInfo = `Converted to WebP (${result.compressionRatio.toFixed(1)}% size reduction)`;
        } catch (error) {
          console.warn('WebP conversion failed, using original:', error);
        }
      }

      // Create filename
      const timestamp = Date.now();
      const sanitizedPrompt = sanitizeFilename(generationPrompt);
      const baseFilename = `private_generated_${sanitizedPrompt}_${timestamp}`;
      const filename = `${baseFilename}.webp`;
      const storagePath = `${user.id}/workbook_images/${filename}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('workbook_images')
        .upload(storagePath, processedFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('workbook_images')
        .getPublicUrl(storagePath);

      // Get image dimensions
      const dimensions = await getImageDimensions(processedFile);

      // Build filter metadata for structured querying
      const activeEnvironmentIds = environmentOptions.filter(o => o.checked).map(o => o.id);
      const activeMoodIds = moodOptions.filter(o => o.checked).map(o => o.id);
      const activeColorIds = colorOptions.filter(o => o.checked).map(o => o.id);
      
      const filterMetadata = {
        styleId: selectedStyle || null,
        influenceId: selectedInfluence || null,
        backgroundId: selectedBackground || null,
        environmentIds: activeEnvironmentIds.length > 0 ? activeEnvironmentIds : null,
        moodIds: activeMoodIds.length > 0 ? activeMoodIds : null,
        colorIds: activeColorIds.length > 0 ? activeColorIds : null,
        imageType: selectedType || null,
        width: dimensions.width || imageWidth,
        height: dimensions.height || imageHeight,
      };

      // Save metadata to database with optional project assignment
      const { data: dbData, error: dbError } = await supabase
        .from('workbook_images')
        .insert({
          user_id: user.id,
          workbook_id: selectedProjectId || null, // Use selected project or NULL for private images
          page_id: null,
          image_url: publicUrl,
          storage_path: storagePath,
          filename,
          file_size: processedFile.size,
          width: dimensions.width || imageWidth,
          height: dimensions.height || imageHeight,
          alt_text: generationPrompt,
          generation_prompt: generationPrompt,
          is_generated: true,
          filter_metadata: filterMetadata, // NEW: Store filter selections for precise querying
        })
        .select()
        .single();

      if (dbError) {
        // Cleanup uploaded file if database insert fails
        await supabase.storage.from('workbook_images').remove([storagePath]);
        throw new Error(`Failed to save image metadata: ${dbError.message}`);
      }

      // NOTE: No automatic Universal Storage integration
      // Users can manually add images to Universal Storage via the "Save to Storage" button
      console.log('Private image saved successfully. Use "Save to Storage" button to add to Universal Storage.');

      // Transform to our interface
      const privateImageItem: PrivateImageGeneratorItem = {
        id: dbData.id,
        user_id: dbData.user_id,
        workbook_id: dbData.workbook_id,
        page_id: null,
        image_url: dbData.image_url,
        storage_path: dbData.storage_path,
        filename: dbData.filename,
        file_size: dbData.file_size,
        width: dbData.width,
        height: dbData.height,
        alt_text: dbData.alt_text,
        generation_prompt: dbData.generation_prompt,
        is_generated: dbData.is_generated,
        created_at: dbData.created_at,
        updated_at: dbData.updated_at,
        deleted_at: null,
      };

      return privateImageItem;
    } catch (error) {
      console.error('Error saving image to private library:', error);
      throw error;
    }
  };

  // Function to generate image using backend prompt building
  const handleGenerateImage = useCallback(async () => {
    console.log('[PrivateImageGenerator] Generating image');
    
    // Validate image description is provided
    if (!imageDescription.trim()) {
      console.log('[PrivateImageGenerator] Empty image description. Cannot generate image.');
      toast({
        title: "Description Required",
        description: "Please provide an image description before generating an image.",
        variant: "destructive",
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to generate images.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setGeneratedImage(null);
    
    try {
      console.log('[PrivateImageGenerator] Calling generateImage with prompt building parameters');
      
      // Collect filter IDs for prompt building
      const activeColorIds = colorOptions.filter(o => o.checked).map(o => o.id);
      const activeEnvironmentIds = environmentOptions.filter(o => o.checked).map(o => o.id);
      const activeMoodIds = moodOptions.filter(o => o.checked).map(o => o.id);
      
      // Use the new utility function
      const result = await generatePrivateImage({
        description: imageDescription,
        width: parseInt(width) || 1024,
        height: parseInt(height) || 1024,
        userId: user.id,
        imageType: selectedType,
        styleId: selectedStyle,
        influenceId: selectedInfluence,
        backgroundId: selectedBackground,
        environmentIds: activeEnvironmentIds,
        moodIds: activeMoodIds,
        colorIds: activeColorIds,
        workbookId: selectedProjectId,
        executeAIOperation,
        onProgress: (message) => {
          console.log('[PrivateImageGenerator]', message);
        },
        onError: (error) => {
          console.error('[PrivateImageGenerator] Generation error:', error);
        }
      });

      setGeneratedImage(result.imageUrl);
      toast({
        title: "Image Generated",
        description: "Your private image has been successfully generated!",
      });
      
      // Set image loading state
      setIsImageLoading(true);
      
      const savedImage = result.savedImage;
      
      // Invalidate queries to refresh the private image library
      queryClient.invalidateQueries({ queryKey: ['private-image-generator'] });
      
      // Trigger quest completion for image generation (Step 3)
      console.log('[PrivateImageGenerator] Triggering quest completion for image generation');
      QuestTriggers.imageGenerated();
      
      if (savedImage) {
        // Create a new Image object to check when it's fully loaded
        const img = new Image();
        img.onload = () => {
          // Image is fully loaded, now open the modal
          setSelectedImage(savedImage);
          setIsImageLoading(false);
        };
        img.onerror = () => {
          // Handle image loading error
          console.error('[PrivateImageGenerator] Error loading image');
          toast({
            title: "Image Loading Failed",
            description: "The image was generated but couldn't be loaded properly.",
            variant: "destructive",
          });
          setIsImageLoading(false);
        };
        // Start loading the image
        img.src = savedImage.image_url;
      } else {
        setIsImageLoading(false);
      }
    } catch (error) {
      console.error('[PrivateImageGenerator] Error generating image:', error);
      toast({
        title: "Generation Failed",
        description: "Failed to generate private image. Please try again.",
        variant: "destructive",
      });
      setIsImageLoading(false);
    } finally {
      setIsGenerating(false);
    }
  }, [
    imageDescription,
    width,
    height,
    selectedType,
    selectedStyle,
    selectedInfluence,
    selectedBackground,
    colorOptions,
    environmentOptions,
    moodOptions,
    selectedProjectId,
    toast,
    user?.id,
    executeAIOperation
  ]);

  return {
    isGenerating: isGenerating || isLoading,
    generatedImage,
    selectedImage,
    setSelectedImage,
    isImageLoading,
    handleGenerateImage,
  };
}; 